import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, User, Tag, Search, BookOpen, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

const fadeUp = {
 initial: { opacity: 0, y: 20 },
 animate: { opacity: 1, y: 0 },
 transition: { duration: 0.5 },
};

const categories = [
 { id: "all", label: "All Posts" },
 { id: "validation", label: "Validation" },
 { id: "product", label: "Product" },
 { id: "founder", label: "Founder Mindset" },
 { id: "scaling", label: "Scaling" },
];

// Fallback static posts for when API has no data
const fallbackPosts = [
 {
  id: "1",
  title: "Why Most MVPs Fail (And What to Build Instead)",
  excerpt: "The problem isn't your MVP. It's that you built before you validated. Here's how to flip the script.",
  category: "validation",
  read_time: "5 min",
  created_at: "2024-01-15",
  author_name: "Let's Build Startup",
 },
 {
  id: "2",
  title: "The Real Cost of Building the Wrong Thing",
  excerpt: "We've watched founders spend $100k+ on products nobody wanted. Here's how to avoid that trap.",
  category: "validation",
  read_time: "7 min",
  created_at: "2024-01-10",
  author_name: "Let's Build Startup",
 },
 {
  id: "3",
  title: "How to Know If Your Idea Is Worth Pursuing",
  excerpt: "Passion isn't enough. Here's a practical framework for evaluating whether your idea has legs.",
  category: "validation",
  read_time: "6 min",
  created_at: "2024-01-05",
  author_name: "Let's Build Startup",
 },
 {
  id: "4",
  title: "Pricing Validation: How to Know What People Will Actually Pay",
  excerpt: "Stop guessing. Here's how to validate your pricing before you build anything.",
  category: "product",
  read_time: "8 min",
  created_at: "2023-12-28",
  author_name: "Let's Build Startup",
 },
 {
  id: "5",
  title: "The Customer Conversation Framework That Actually Works",
  excerpt: "Most founder interviews are useless. Here's how to have conversations that reveal real insights.",
  category: "validation",
  read_time: "9 min",
  created_at: "2023-12-20",
  author_name: "Let's Build Startup",
 },
 {
  id: "6",
  title: "When to Pivot (And When to Persist)",
  excerpt: "The hardest founder decision: knowing when to keep going vs. when to change direction.",
  category: "founder",
  read_time: "6 min",
  created_at: "2023-12-15",
  author_name: "Let's Build Startup",
 },
];

export default function BlogPage() {
 const [searchQuery, setSearchQuery] = useState("");
 const [activeCategory, setActiveCategory] = useState("all");
 const [posts, setPosts] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);
 const [usingFallback, setUsingFallback] = useState(false);

 const fetchPosts = useCallback(async () => {
  setLoading(true);
  setError(null);
  
  try {
   const params = new URLSearchParams();
   if (activeCategory !== "all") {
    params.append("category", activeCategory);
   }
   
   const url = `${API_URL}/api/blogs/public${params.toString() ? `?${params.toString()}` : ""}`;
   const response = await fetch(url);
   
   if (!response.ok) {
    throw new Error("Failed to fetch blog posts");
   }
   
   const data = await response.json();
   
   if (data.length === 0) {
    // No posts from API, use fallback
    setUsingFallback(true);
    setPosts(fallbackPosts);
   } else {
    setUsingFallback(false);
    setPosts(data);
   }
  } catch (err) {
   console.error("Error fetching blogs:", err);
   // Fallback to static data on error
   setUsingFallback(true);
   setPosts(fallbackPosts);
  } finally {
   setLoading(false);
  }
 }, [activeCategory]);

 useEffect(() => {
  fetchPosts();
 }, [fetchPosts]);

 // Filter posts based on search query
 const filteredPosts = posts.filter((post) => {
  const matchesSearch = searchQuery === "" || 
   post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
   post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
  const matchesCategory = activeCategory === "all" || post.category === activeCategory;
  return matchesSearch && matchesCategory;
 });

 const formatDate = (dateString) => {
  try {
   return new Date(dateString).toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
   });
  } catch {
   return dateString;
  }
 };

 return (
  <div className="flex flex-col">
   {/* Hero */}
   <section className="bg-hero section-padding">
    <div className="container-narrow">
     <motion.div
      initial="initial"
      animate="animate"
      variants={fadeUp}
      className="text-center"
     >
      <h1 className="heading-display text-foreground mb-6">
       Blog
      </h1>
      <p className="body-large text-muted-foreground max-w-2xl mx-auto">
       Honest insights on building, validating, and scaling, from founders who've been there.
      </p>
     </motion.div>
    </div>
   </section>

   {/* Filters */}
   <section className="bg-background py-8 border-b border-border/50">
    <div className="container-base">
     <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
      <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full md:w-auto">
       <TabsList className="w-full md:w-auto flex-wrap h-auto gap-1 bg-transparent p-0">
        {categories.map((cat) => (
         <TabsTrigger
          key={cat.id}
          value={cat.id}
          className="data-[state=active]:bg-accent data-[state=active]:text-accent-foreground rounded-full px-4 py-1.5 text-sm"
          data-testid={`category-filter-${cat.id}`}
         >
          {cat.label}
         </TabsTrigger>
        ))}
       </TabsList>
      </Tabs>
      
      <div className="relative w-full md:w-64">
       <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
       <Input
        placeholder="Search posts..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="pl-9"
        data-testid="blog-search-input"
       />
      </div>
     </div>
    </div>
   </section>

   {/* Posts */}
   <section className="bg-background section-padding">
    <div className="container-base">
     {loading ? (
      <div className="flex flex-col items-center justify-center py-16">
       <Loader2 className="h-8 w-8 text-accent animate-spin mb-4" />
       <p className="text-muted-foreground">Loading posts...</p>
      </div>
     ) : filteredPosts.length > 0 ? (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
       {filteredPosts.map((post, i) => (
        <motion.div
         key={post.id}
         initial={{ opacity: 0, y: 20 }}
         whileInView={{ opacity: 1, y: 0 }}
         viewport={{ once: true }}
         transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
        >
         <Link to={`/blog/${post.id}`} data-testid={`blog-card-${post.id}`}>
          <Card className="card-elevated h-full flex flex-col group cursor-pointer">
           <CardContent className="p-6 flex flex-col flex-1">
            <div className="flex items-center gap-2 mb-3">
             <Badge variant="outline" className="text-xs">
              {categories.find(c => c.id === post.category)?.label || post.category}
             </Badge>
             <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {post.read_time || "5 min"}
             </span>
            </div>
            
            <h3 className="font-serif text-lg font-medium text-foreground mb-2 group-hover:text-accent transition-colors">
             {post.title}
            </h3>
            
            <p className="text-sm text-muted-foreground mb-4 flex-1">
             {post.excerpt}
            </p>
            
            <div className="flex items-center justify-between pt-4 border-t border-border/50 mt-auto">
             <span className="text-xs text-muted-foreground">
              {formatDate(post.published_at || post.created_at)}
             </span>
             <span className="text-sm font-medium text-accent flex items-center gap-1">
              Read more
              <ArrowRight className="h-3 w-3" />
             </span>
            </div>
           </CardContent>
          </Card>
         </Link>
        </motion.div>
       ))}
      </div>
     ) : (
      <div className="text-center py-16">
       <BookOpen className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
       <p className="text-muted-foreground">No posts found matching your criteria.</p>
       <Button
        variant="outline"
        size="sm"
        className="mt-4"
        onClick={() => { setSearchQuery(""); setActiveCategory("all"); }}
        data-testid="clear-filters-btn"
       >
        Clear filters
       </Button>
      </div>
     )}
    </div>
   </section>

   {/* CTA */}
   <section className="bg-section section-padding-sm">
    <div className="container-narrow text-center">
     <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
     >
      <h2 className="heading-section text-foreground mb-4">Have a Question We Haven't Answered?</h2>
      <p className="body-base text-muted-foreground mb-6">
       Reach out. We might write about it.
      </p>
      <Button variant="accent" size="lg" asChild>
       <Link to="/contact" data-testid="blog-cta-contact">
        Get in Touch
        <ArrowRight className="h-4 w-4 ml-1" />
       </Link>
      </Button>
     </motion.div>
    </div>
   </section>
  </div>
 );
}
